import {StyleSheet} from 'react-native';

// Colors match the design handoff's tokens
// (chess repo docs/design/player-profile/README.md "Design Tokens" section):
// cream #FAF6EB, brown #4D3F37, yellow #FACF47, aqua #3DAB9E, orange #E17846.
export default StyleSheet.create({
  // Spec §2 hero card: radius 24, padding 28/30, soft card shadow, no border.
  // overflow hidden clips the sweeping HeroSheen band to the rounded card.
  card: {
    backgroundColor: '#fff',
    borderRadius: 24,
    paddingVertical: 28,
    paddingHorizontal: 30,
    marginBottom: 18,
    overflow: 'hidden',
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sheen: {
    position: 'absolute',
    top: -30,
    bottom: -30,
    left: 0,
    width: 220,
  },
  heroRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    // Top-align so the rating card sits at its own content height (design's
    // `align-items:flex-start`), not stretched to match the taller identity
    // column -- stretching left a big empty gap above the sparkline.
    alignItems: 'flex-start',
    gap: 24,
  },
  leftCol: {
    flex: 1,
    // With flexWrap on, a real minWidth lets the rating card drop below the
    // identity block on narrow viewports instead of crushing it.
    minWidth: 320,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  // The solid inner circle -- the dashed ring is now a separate rotating
  // AvatarRing (size 118, matching the design's ring) that this sits inside,
  // inset ~9px like the design.
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3DAB9E',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  // Everything in this column (name, meta line, stat pills, CTA row) shares
  // this one indent, starting to the right of the avatar -- per the design
  // handoff, stat pills/buttons are NOT flush under the avatar's left edge.
  identityCol: {
    flex: 1,
    marginLeft: 26,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  name: {
    fontSize: 30,
    color: '#262322',
  },
  fideChip: {
    backgroundColor: '#4D3F37',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 7,
    marginLeft: 10,
  },
  fideChipText: {
    color: '#FACF47',
    fontSize: 11,
    fontWeight: '600',
  },
  memberSince: {
    fontSize: 14,
    color: '#888280',
    marginTop: 4,
  },
  // Design's identity column has a 9px inter-row gap PLUS a 6px margin above
  // the stats row -- ~15px total. (Earlier I read "spacing is more" as
  // "too much" and shrank it; it meant the gap should be larger.)
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 9,
    marginTop: 15,
  },
  statChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAF6EB',
    borderRadius: 11,
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  statLabel: {
    fontSize: 13,
    lineHeight: 16,
    color: '#4D3F37',
  },
  statValue: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '700',
    color: '#262322',
  },
  // Design: 9px column gap + 12px margin above the button row -- ~21px.
  ctaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 9,
    marginTop: 21,
  },
  // CTA button colors/padding/weights now live in the shared ProfileButton
  // wrapper (components/ui/ProfileButton.tsx) -- the row here only lays them
  // out.
  shareButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Owner-only "more" menu (kebab, right after the share icon): Public/
  // Private toggle + "View as" preview picker. Rendered in a Modal (escapes
  // the header card's overflow:hidden, needed for HeroSheen's sweep) and
  // absolutely positioned at the button's own measured coordinates (set at
  // the call site) so it actually anchors to the kebab button.
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(38,35,34,0.15)',
  },
  menuDropdown: {
    minWidth: 220,
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.12)',
    paddingVertical: 8,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.14,
    shadowRadius: 12,
    elevation: 6,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  menuItemText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4D3F37',
  },
  menuDivider: {
    height: 1,
    backgroundColor: 'rgba(38,35,34,0.1)',
    marginVertical: 6,
  },
  menuSectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#888280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 14,
    marginBottom: 2,
  },
  // Own toast for the share button (replaces the browser's native alert()).
  // `fixed` + bottom-center, same escape-the-card-clip trick as menuDropdown.
  shareToast: {
    position: 'fixed' as any,
    left: 0,
    right: 0,
    bottom: 28,
    alignItems: 'center',
    zIndex: 20,
  },
  shareToastText: {
    backgroundColor: '#4D3F37',
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 999,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 6,
  },
});
